/**
 * The account the dashboard heading is describing.
 *
 * 'Account: Company Name Undefined' was not a missing field. An admin has no company: the API's
 * company_name is a SerializerMethodField that looks up the CustomerUser row behind the user and returns
 * '' when there is none (tape/auth/serializers.py, get_company_name), and an admin has no such row. The
 * heading then fell through to its filler text and read as a broken page.
 *
 * A customer user still sees their own company, which is the only account they have. An admin is not
 * looking at an account, they are looking at a scope — whatever the header's job selector is pointed at —
 * so that is what the line names: the client whose jobs are being counted, or all of them. Which also
 * makes the heading agree with the tiles beneath it instead of restating something about the login.
 */

/** What an admin who has not narrowed the selector is looking at, worded as the AccountCreation page does. */
export const ALL_CUSTOMERS_HEADING = 'All Customers';

/**
 * @param {object} [args]
 * @param {object} [args.user] - The authenticated user, as AuthProvider holds it.
 * @param {boolean} [args.isAdmin] - Whether the user is an admin, and so has no company of their own.
 * @param {object[]} [args.jobGroups] - The selector's groups, {key: cid, label: company name, items}.
 * @param {number[]} [args.selectedCids] - The customer ids the current selection spans.
 * @returns {string} The name for the heading, or '' when there is nothing truthful to put there — the
 *          caller drops the 'Account:' clause rather than printing a placeholder.
 */
export function accountHeadingName({user, isAdmin = false, jobGroups = [], selectedCids = []} = {}) {
    const company = typeof user?.company_name === 'string' ? user.company_name.trim() : '';

    // A customer user's own company, unchanged. Checked before the admin branch rather than after, so a
    // company name is never overridden by a selection — and an admin never has one to be preferred.
    if (company) return company;

    /*
     * Not an admin and no company name either: a Customer row with an empty company_name, which is a data
     * problem this heading cannot fix and should not paper over. '' tells the caller to say nothing, which
     * is better than naming a scope this reader does not have — a customer sees only their own media
     * whatever the selector says.
     */
    if (!isAdmin) return '';

    const labels = selectedCids
        .map(cid => (jobGroups || []).find(group => Number(group?.key) === Number(cid))?.label)
        .filter(label => typeof label === 'string' && label.trim() !== '');

    // One client in scope: name it. Several: count them, because the heading is one line shared with the
    // user's name and a list of six companies would push the reader's own name off the end of it.
    if (labels.length === 1) return labels[0];
    if (labels.length > 1) return `${labels.length} customers`;

    /*
     * Nothing selected — and also the first paint, where a selection restored from the session is held
     * before the selector's list has arrived to name it. Both are 'everything the portal holds', which is
     * what the aggregate on the page is showing at that moment, so the same words are right for both.
     */
    return ALL_CUSTOMERS_HEADING;
}
